import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Configure PDF.js worker (must be in same module as Document/Page). Use CDN for CRA.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


type MobilePdfViewerProps = {
  pdfUrl: string;
  containerClassName?: string;
  openLinksInNewTab?: boolean;
};

const MobilePdfViewer: React.FC<MobilePdfViewerProps> = ({
  pdfUrl,
  containerClassName = '',
  openLinksInNewTab = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoadError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    setLoadError(error?.message || 'Failed to load PDF');
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = () => {
      const w = el.getBoundingClientRect().width;
      setContainerWidth(w);
    };

    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!openLinksInNewTab) return;
    const el = containerRef.current;
    if (!el) return;

    const applyLinkTarget = () => {
      const links = el.querySelectorAll<HTMLAnchorElement>('.react-pdf__Page__annotations a[href]');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    };

    applyLinkTarget();
    const observer = new MutationObserver(applyLinkTarget);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [openLinksInNewTab, numPages, containerWidth]);

  return (
    <div
      ref={containerRef}
      className={`overflow-auto overscroll-contain mobile-pdf-viewer ${containerClassName}`}
      style={{
        height: '100%',
        minHeight: '-webkit-fill-available',
        paddingBottom: 'max(env(safe-area-inset-bottom), 52px)',
      }}
    >
      <style>{`
        .mobile-pdf-viewer .mobile-pdf-pages .react-pdf__Page {
          margin: 0;
          padding: 0;
        }
        .mobile-pdf-viewer .react-pdf__message {
          min-height: 0;
          padding: 0.5rem 0;
        }
      `}</style>
      {containerWidth > 0 && (
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          className="mobile-pdf-document"
          loading={
            <div className="flex items-center justify-center py-16 text-white/80">
              Loading report…
            </div>
          }
          error={
            <div className="py-8 text-center text-red-300">
              {loadError || 'Failed to load PDF'}
            </div>
          }
        >
          <div className="mobile-pdf-pages" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Array.from(new Array(numPages), (_, i) => (
              <Page
                key={`page_${i + 1}`}
                pageNumber={i + 1}
                width={containerWidth}
                renderTextLayer={false}
                renderAnnotationLayer={true}
                className="mobile-pdf-page"
              />
            ))}
          </div>
        </Document>
      )}
    </div>
  );
};

export default MobilePdfViewer;
